<template lang="pug">
#product-list-container
  template(v-if='paginatedProducts.length === 0')
    CharacterMessages(
      messageContent='OOPS! NOTHING TO SEE HERE <br>Just a clown and his honk.',
      characterImage='filter-error.png'
    )

  template(v-if='paginatedProducts.length > 0')
    .product-item.desktop(
      v-for='product in paginatedProducts',
      :style='{ flex: itemDisplayedPerRow }'
    )
      router-link(:to='"/product/" + product.slug' @click.native='fireSelectItemEvent(product)')
        .product-blurb-icon
          img.new-icon(v-if='product.is_new', src='../assets/images/icons/new-blurb.png')
          img.sales-icon(v-if='product.discounted_price', src='../assets/images/icons/sale-blurb.png')
        img.product-image(
          v-if='product.image',
          :src='`/uploads/images/products/${product.image}`',
          @error='loadPlaceholder($event)'
        )
        p {{ product.displayText }}
      .bottom-container
        .price-container
          // if usual price
          template(v-if='!product.discounted_price && product.is_sale !== 1')
            p.price(v-if='!product.discounted_price') RM{{ priceFormat(product.price) }}

          // if product has discounted price
          template(v-if='product.discounted_price && product.is_sale === 1')
            p.price RM{{ priceFormat(product.discounted_price) }}
            p.usual-price #[del RM{{ priceFormat(product.price) }}]
        .row
          .button-container
            button.add-to-cart(v-if='product.stock > 0', @click='addToCart(product)') Add to Cart {{ quantityInCart(product.id) }}
            button.out-of-stock(v-else) Coming Soon
          .favourite-icon-container(v-if='currentPage === "favourite"')
            img.favourite-icon(
              src='../assets/images/icons/favourite-round-red.png',
              @click='removeWishlistItem(product.id)',
              gtm-label="products.favourite-remove-wishlist"
            )
          .favourite-icon-container(v-if='currentPage === "product"')
            img.favourite-icon(
              v-if='inWishList(product.id)',
              src='../assets/images/icons/favourite-round-red.png',
              @click='removeWishlistItem(product.id, product.slug)',
              gtm-label="products.remove-wishlist"
            )
            img.favourite-icon(
              v-if='!inWishList(product.id)',
              src='../assets/images/icons/favourite-round-grey.png',
              @click='addToWishlist(product.id, product.slug)'
            )

    .product-item.mobile(
      :to='"/product/" + product.slug',
      v-for='product in paginatedProducts',
      :style='{ flex: mobileItemDisplayedPerRow }'
    )
      router-link(:to='"/product/" + product.slug' @click.native='fireSelectItemEvent(product)')
        .product-blurb-icon
          img.new-icon(v-if='product.is_new', src='../assets/images/icons/new-blurb.png')
          img.sales-icon(v-if='product.discounted_price', src='../assets/images/icons/sale-blurb.png')
        img.product-image(
          v-if='product.image',
          :src='`/uploads/images/products/${product.image}`',
          @error='loadPlaceholder($event)'
        )
        p {{ product.displayText }}
      .bottom-container
        .price-container
          // if usual price
          template(v-if='!product.discounted_price && product.is_sale !== 1')
            p.price(v-if='!product.discounted_price') RM{{ priceFormat(product.price) }}

          // if product has discounted price
          template(v-if='product.discounted_price && product.is_sale === 1')
            p.price RM{{ priceFormat(product.discounted_price) }}
            p.usual-price #[del RM{{ priceFormat(product.price) }}]
        .row
          .button-container
            button.add-to-cart(v-if='product.stock > 0', @click='addToCart(product)') Add to Cart {{ quantityInCart(product.id) }}
            button.out-of-stock(v-if='product.stock <= 0') Coming Soon
          .favourite-icon-container(v-if='currentPage === "favourite"')
            img.favourite-icon(
              src='../assets/images/icons/favourite-round-red.png',
              @click='removeWishlistItem(product.id)',
              gtm-label="products.favourite-remove-wishlist"
            )
          .favourite-icon-container(v-if='currentPage === "product"')
            img.favourite-icon(
              v-if='inWishList(product.id)',
              src='../assets/images/icons/favourite-round-red.png',
              @click='removeWishlistItem(product.id, product.slug)',
              gtm-label="products.remove-wishlist"
            )
            img.favourite-icon(
              v-if='!inWishList(product.id)',
              src='../assets/images/icons/favourite-round-grey.png',
              @click='addToWishlist(product.id, product.slug)'
            )

    .pagination-container.desktop(v-if='enablePagination && numberOfPages > 0')
      .pagination-index(@click='previousPage' gtm-label="products.pagination-previous")
        img(src='../assets/images/icons/arrow-left.png')
      .pagination-index(
        v-for='i in numberOfPages',
        @click='paginationIndex = i - 1',
        :class='{ active: i === paginationIndex + 1 }'
      )
        span {{ i }}
      .pagination-index(@click='nextPage' gtm-label="products.pagination-next")
        img(src='../assets/images/icons/arrow-right.png')

  .mobile-pagination-container.mobile(v-if='enablePagination && numberOfPages > 0')
    span.pagination-button-container #[span.button(@click='previousPage' gtm-label="products.pagination-previous") &lt; &nbsp; Previous &nbsp;] | #[span.button(v-if='paginationIndex < numberOfPages - 1', @click='nextPage' gtm-label="products.pagination-next") &nbsp; Next &nbsp; &gt;]
</template>

<script>
export default {
  props: ['products', 'itemPerRow', 'mobileItemPerRow', 'favouritePage', 'currentPage', 'enablePagination'],

  components: {
    CharacterMessages: () => import('../components/character-messages.vue')
  },

  data () {
    return {
      paginationIndex: 0
    }
  },

  watch: {
    '$store.state.sortValue' () {
      this.paginationIndex = 0
    },

    $route (to, from) {
      this.paginationIndex = 0
    }
  },

  computed: {
    numberOfPages () {
      if (this.allSortedProducts.length < 12) {
        return 0
      } else {
        return Math.ceil(this.allSortedProducts.length / 12)
      }
    },

    itemDisplayedPerRow () {
      return `0 0 ${100 / this.itemPerRow}%`
    },

    mobileItemDisplayedPerRow () {
      return `0 0 ${100 / this.mobileItemPerRow}%`
    },

    allSortedProducts () {
      let sortedProducts = []

      // clone existing products
      let productList = this.products.slice()

      if (!this.$store.state.sortValue) {
        sortedProducts = productList
      }

      if (this.$store.state.sortValue === 'available') {
        sortedProducts = productList.filter((product) => product.stock > 0)
      }

      if (this.$store.state.sortValue === 'latest') {
        sortedProducts = productList.sort((a, b) => {
          return b.created - a.created
        })
      }

      if (this.$store.state.sortValue === 'priceAsc') {
        sortedProducts = productList.sort((a, b) => {
          return (a.discounted_price || a.price) - (b.discounted_price || b.price)
        })
      }

      if (this.$store.state.sortValue === 'priceDesc') {
        sortedProducts = productList.sort((a, b) => {
          return (b.discounted_price || b.price) - (a.discounted_price || a.price)
        })
      }

      if (this.$store.state.sortValue === 'brickAsc') {
        sortedProducts = productList.sort((a, b) => {
          return a.brick - b.brick
        })
      }

      if (this.$store.state.sortValue === 'brickDesc') {
        sortedProducts = productList.sort((a, b) => {
          return b.brick - a.brick
        })
      }

      if (this.$store.state.filterEnabled) {
        let filtered = []

        if (this.$store.state.filter.price) {
          filtered = sortedProducts.filter((product) => {
            // TODO filter price
            return (
              (product.discounted_price || product.price) > this.$store.state.filter.price[0] &&
              (product.discounted_price || product.price) < this.$store.state.filter.price[1] &&
              product.brick > this.$store.state.filter.brickcount[0] &&
              product.brick < this.$store.state.filter.brickcount[1]
            )
          })
        }
        return filtered
      }

      return sortedProducts
    },

    paginatedProducts () {
      if (this.enablePagination) {
        let paginatedProducts = []
        paginatedProducts = this.allSortedProducts.slice(this.paginationIndex * 12, this.paginationIndex * 12 + 12)
        return paginatedProducts
      }

      return this.allSortedProducts
    }
  },

  methods: {
    priceFormat (num) {
      return num
        .toFixed(2)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    loadPlaceholder (event) {
      event.target.src = `/uploads/images/placeholder.png`
    },

    async addToCart (product) {
      this.$store.commit('setLoadingState', true)
      let FromFavourite = false
      try {
        if (this.currentPage === 'favourite') {
          FromFavourite = true
          await this.$store.dispatch('addToCartFromWishlist', product.id)
          this.$store.commit('enableCartDropdown')
        }

        if (this.currentPage === 'product') {
          await this.$store.dispatch('addToCart', { productId: product.id })
          this.$store.commit('enableCartDropdown')
        }

        if (
          product.selectableGifts &&
          product.selectableGifts.length > 0 &&
          !this.$store.state.cart.items.find((item) =>
            product.selectableGifts.find((reward) => reward.id === item.productId)
          )
        ) {
          this.$store.commit('setSelectableGifts', product)
          this.$store.commit('activateModal', { modalName: 'freeGift' })
        }

        this.$store.commit('setLoadingState', false)
        this.$store.commit('enableCartDropdown')

        // GA4 Ecommerce: add_to_cart
        // dataLayer.push({
        //   event: 'add_to_cart',
        //   ecommerce: {
        //     items: [
        //       {
        //         item_name: product.name,
        //         item_id: product.identifier,
        //         price: product.price,
        //         item_brand: 'lego',
        //         item_category: '',
        //         item_category2: '',
        //         item_category3: '',
        //         item_category4: '',
        //         item_list_name: '',
        //         item_list_id: '',
        //         index: '',
        //         quantity: '1'
        //       }
        //     ]
        //   }
        // })
        dataLayer.push({
          from_favourite: undefined,
          product_slug: undefined,
          search_value: undefined,
          sort_value: undefined,
          from: undefined,
          ecommerce: null
        })
        dataLayer.push({
          event: 'add_to_cart',
          user_email: this.$store.getters.getUserEmail,
          from_favourite: FromFavourite,
          product_slug: product.slug,
          ecommerce: {
            currency: 'MYR',
            value: product.price,
            items: [{
              item_name: product.name,
              item_id: product.identifier,
              price: product.price,
              quantity: 1,
              item_brand: 'lego',
              item_category: ''
            }]
          }
        })
        // End GA4 Ecommerce: add_to_cart
      } catch (error) {
        this.$store.commit('setLoadingState', false)
        this.$store.commit('activateModal', {
          modalName: 'message',
          textContent: error.response.data,
          characterImageOne: 'notify-body',
          characterImageTwo: 'notify-hand',
          ctaText: 'Continue Shopping',
          ctaAction: () => window.location.reload()
        })
      }
    },

    async addToWishlist (productId, slug) {
      // this.$store.commit('setLoadingState', true)
      await this.$store.dispatch('addToFavourites', productId)
      // this.$store.commit('setLoadingState', false)
      dataLayer.push({
        from_favourite: undefined,
        product_slug: undefined,
        search_value: undefined,
        sort_value: undefined,
        from: undefined,
        ecommerce: null
      })
      dataLayer.push({
        event: 'add_to_wishlist',
        user_email: this.$store.getters.getUserEmail,
        from: 'products',
        product_slug: product.slug
      })
    },

    async removeWishlistItem (productId, slug) {
      // this.$store.commit('setLoadingState', true)
      if (this.currentPage === 'product') {
        const selectedProduct = this.$store.state.favouriteItems.find((item) => item.product_id === productId)
        await this.$store.dispatch('removeWishlistItem', selectedProduct.id)
      }

      if (this.currentPage === 'favourite') {
        await this.$store.dispatch('removeWishlistItem', productId)
      }

      // this.$store.commit('setLoadingState', false)
    },

    quantityInCart (productId) {
      const selectedProduct = this.$store.state.cart.items.find((cartItem) => cartItem.productId === productId)
      if (selectedProduct) {
        if (selectedProduct.quantity) {
          return '(' + selectedProduct.quantity + ')'
        } else {
          return ''
        }
      }
    },

    inWishList (productId) {
      const selectedProduct = this.$store.state.favouriteItems.find((item) => item.product_id === productId)
      if (selectedProduct) {
        return true
      } else {
        return false
      }
    },

    previousPage () {
      if (this.paginationIndex > 0) {
        this.paginationIndex--
        document.querySelector('#product-list-container').scrollIntoView()
      }
    },

    nextPage () {
      if (this.paginationIndex < this.numberOfPages - 1) {
        this.paginationIndex++
        document.querySelector('#product-list-container').scrollIntoView()
      }
    },

    // GA4 Ecommerce: select_item
    fireSelectItemEvent (product) {
      dataLayer.push({
        from_favourite: undefined,
        product_slug: undefined,
        search_value: undefined,
        sort_value: undefined,
        from: undefined,
        ecommerce: null
      })
      dataLayer.push({
        event: 'select_item',
        user_email: this.$store.getters.getUserEmail,
        product_slug: product.slug,
        ecommerce: {
          items: [
            {
              item_name: product.name,
              item_id: product.identifier,
              price: product.price,
              item_brand: 'lego',
              item_category: '',
              item_category2: '',
              item_category3: '',
              item_category4: '',
              item_list_name: '',
              item_list_id: '',
              index: '',
              quantity: '1'
            }
          ],
          currency: 'MYR'
        }
      })
    }
    // End GA4 Ecommerce: select_item
  }
}
</script>

<style lang="sass" scoped>
@import '../sass/variables.sass'

.no-results-container
  display: flex
  margin: 30px auto 0 auto
  +mobile
    flex-direction: column
  .image-container
    width: 250px
    img
      width: 100%
  .text-container
    .empty
      text-align: center
      font-size: 22px
      font-weight: 400
      padding: 160px 40px
      max-width: 500px
      display: block
      margin: 0 auto
      +mobile
        padding: 40px 0px

#product-list-container
  display: flex
  flex-wrap: wrap
  padding: 10px 40px 80px 40px
  position: relative
  +mobile
    padding: 0px
    // margin: 10px 80px 0 20px
    margin-bottom: 10px 1
    overflow: hidden
  .product-item
    // flex: 0 0 25%
    padding: 12px 12px 115px 12px
    box-sizing: border-box
    position: relative
    margin-bottom: 20px
    +mobile
      // flex: 0 0 100%
      padding: 0 10px 105px 10px
      margin-bottom: 20px
    p
      +mobile
        font-size: 14px
    .product-blurb-icon
      position: absolute
      top: 10px
      left: 10px
      +mobile
        top: 0
        left: 0
      .new-icon, .sales-icon
        padding: 0 3px
        width: 40px
        +mobile
          width: 30px
    .product-image
      width: 100%
    .product-description
      +mobile
        font-size: 18px
    .product-price
      font-weight: 900
      font-size: 26px
      color: #212121
      margin: 20px 0

    .bottom-container
      align-items: center
      position: absolute
      bottom: 0
      left: 0
      width: 100%
      padding: 0 12px
      box-sizing: border-box
      +mobile
        padding: 0 20px 0 10px
      .product-price
        margin-bottom: 5px
        margin-top: 0
      .row
        display: flex
        align-items: center
        .button-container
          flex-grow: 1
          button
            width: 100%
      .price-container
        font-size: 20px
        line-height: 20px
        font-weight: 900
        margin: 0 0 15px 0
        +mobile
          font-size: 18px
          line-height: 18px
          margin: 0 0 15px 0
        .price
          display: inline-block
        .usual-price
          display: inline-block
          color: #E3000B
          font-size: 16px
          line-height: 18px
          margin-left: 20px
          font-weight: 400
          +mobile
            margin-left: 15px
            font-size: 14px
            line-height: 16px
      button
        border-radius: 0
        color: white
        text-align: center
        font-size: 18px
        font-weight: 500
        border: none
        padding-top: 15px
        padding-bottom: 15px
        flex-grow: 1
        outline: none
        cursor: pointer
        &.add-to-cart
          background-color: #EE7500
        &.out-of-stock
          background-color: #545454
        +mobile
          font-size: 13px
          padding-top: 10px
          padding-bottom: 10px

      .favourite-icon-container
        margin-left: 10px
        margin-right: 20px
        cursor: pointer
        +mobile
          margin-left: 5px
          margin-right: 0
        img
          width: 50px
          +mobile
            width: 35px
        .favourite-icon
          +mobile
            display: flex

  .pagination-container
    display: flex
    position: absolute
    bottom: 0
    left: 51px
    .pagination-index
      cursor: pointer
      background-color: $yellow
      padding: 16px 20.5px
      border: 0.2px solid white
      &.active
        background-color: #ED1C24
        color: white

  .mobile-pagination-container
    display: block
    margin: 20px auto 0 auto
    +mobile
      width: 100%
    .pagination-button-container
      margin: 0 auto
      display: table
    .button
      cursor: pointer

.error-messages
  color: #E3000B
  margin-top: 5px
  padding-left: 0
  position: absolute
  bottom: -25px
  li
    font-size: 11px
    line-height: 10px
    font-style: italic
    font-weight: 100
    margin-bottom: 8px
    position: relative
</style>
